import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// FastAPI 422 returns an array of {msg,...} objects — never render raw objects
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full bg-[#0c0c0c] border border-[#3a3a3a] focus:border-[#cfff04] focus:outline-none px-3 py-2.5 text-sm placeholder:text-[#d0d0d0]";
const labelCls = "font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] block mb-1.5";

/** Email/password sign-in + sign-up form. Owns its own field state. */
export const EmailPasswordForm = ({ mode, onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "signup" && password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } =
        mode === "signin"
          ? await api.post("/auth/login", { email, password })
          : await api.post("/auth/register", {
              name, email, password,
              ref_code: localStorage.getItem("spur_ref") || null,
            });
      toast.success(mode === "signin" ? `Welcome back, ${data.name || data.email}` : "Account created — you're in!");
      setPassword("");
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {mode === "signup" && (
        <div>
          <label className={labelCls}>Name</label>
          <input
            data-testid="auth-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Satoshi"
            maxLength={60}
            required
            className={inputCls}
          />
        </div>
      )}
      <div>
        <label className={labelCls}>Email</label>
        <input
          data-testid="auth-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          maxLength={120}
          required
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>Password {mode === "signup" && <span className="text-[#d0d0d0] normal-case">(min 8 chars)</span>}</label>
        <input
          data-testid="auth-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          maxLength={128}
          required
          className={inputCls}
        />
      </div>

      {error && (
        <p data-testid="auth-error" className="font-mono text-[11px] text-red-400 leading-relaxed">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="auth-submit-btn"
        className="w-full btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm py-3.5 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {busy && <Loader2 size={14} className="animate-spin" />}
        {mode === "signin" ? "Sign in" : "Create account"}
      </button>
    </form>
  );
};
